import { ArrowForward, AutoAwesome, CheckCircleOutlined, LockOutlined, PlayArrow } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Container, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { AUTH_ROUTES, PORTAL_ROUTES } from '../routes';
import './LandingPage.css';

const processSteps = [
  {
    id: '01',
    title: 'Start one trip for everyone traveling',
    description:
      'Choose the destination, dates, and travelers once. Ta2shera turns that into one coordinated visa workspace.',
  },
  {
    id: '02',
    title: 'Upload documents once and reuse them',
    description:
      'Passports, photos, payslips, and supporting files stay organized by traveler so every new application moves faster.',
  },
  {
    id: '03',
    title: 'Track requirements, packages, and orders in one place',
    description:
      'From AI validation to printed packages and add-on services, the whole journey stays visible without scattered chats or spreadsheets.',
  },
];

const capabilityCards = [
  {
    title: 'Trip workspace',
    description: 'Build visa journeys for solo travelers or families with clear dates, status, and next actions.',
  },
  {
    title: 'Reusable document vault',
    description: 'Keep uploaded files structured by traveler and document type so future applications start from a stronger baseline.',
  },
  {
    title: 'Requirement tracking',
    description: 'See what is ready, what is pending AI validation, and what still needs supporting documents before submission.',
  },
  {
    title: 'Operational follow-up',
    description: 'Manage translations, appointment assistance, printed packages, and delivery updates from the same portal.',
  },
];

const serviceCards = [
  {
    title: 'Notarised translation to your doorstep',
    description:
      'Order certified translation support for key application documents and follow the request from processing to delivery.',
  },
  {
    title: 'Schengen photos to your doorstep',
    description:
      'Request compliant visa photo support and receive the finished set without adding another errand to the process.',
  },
  {
    title: 'Printed visa package to your doorstep',
    description:
      'Get a printed version of your prepared visa package delivered when you want a ready-to-carry submission set.',
  },
  {
    title: 'Visa appointment scheduling',
    description:
      'Use appointment support services when you want help coordinating the booking step as part of the wider journey.',
  },
];

const trustPoints = [
  'AI helps prepare the workflow, but the journey stays reviewable step by step.',
  'Family-based organization reduces duplicate uploads and missed paperwork.',
  'Every major artifact lives in one workspace instead of being buried in chat threads.',
];

const faqItems = [
  {
    question: 'What does Ta2shera help with?',
    answer:
      'It brings trip planning, family records, reusable documents, visa requirement tracking, and operational follow-up into one guided workspace.',
  },
  {
    question: 'Can I manage multiple travelers together?',
    answer:
      'Yes. The portal is built around family and traveler context, so one trip can coordinate documents, requirements, and follow-up across several people.',
  },
  {
    question: 'Do I need to re-upload everything for each trip?',
    answer:
      'No. The experience is designed around reusable documents, so future applications can start from what is already stored, organized, and linked to each traveler.',
  },
  {
    question: 'Which countries are currently supported?',
    answer:
      'Ta2shera currently supports visa journeys for The Netherlands. Support for additional countries is planned as the product expands.',
  },
  {
    question: 'Is Ta2shera free to use?',
    answer:
      'Yes. The core product is free to use for organizing trips, documents, and visa requirements. Some optional add-on services, such as notarised translations, appointment assistance, insurance, or printed visa packages, may have separate fees when you choose to order them.',
  },
  {
    question: 'How do paid services work?',
    answer:
      'Paid services are optional. If you need extra help beyond the free workspace, you can place orders for selected services and track their status, documents, and totals directly inside the portal.',
  },
  {
    question: 'Can I use the product without buying add-ons?',
    answer:
      'Absolutely. You can use the workspace on its own to prepare and organize your application, and only choose add-on services if they are useful for your case.',
  },
];

function FullWidthSection({ children, className = '' }) {
  return (
    <Container className={`landing-container ${className}`.trim()} disableGutters maxWidth={false}>
      {children}
    </Container>
  );
}

function TripsPreview() {
  return (
    <div className="landing-preview landing-preview--trips">
      <div className="landing-preview__sidebar">
        <div className="landing-preview__brand">TA2SHERA</div>
        <div className="landing-preview__nav">
          <div className="landing-preview__nav-label">Workspace</div>
          <div className="landing-preview__nav-item landing-preview__nav-item--active">My Trips</div>
          <div className="landing-preview__nav-item">Documents</div>
          <div className="landing-preview__nav-item">Family</div>
          <div className="landing-preview__nav-item">Orders</div>
        </div>
      </div>
      <div className="landing-preview__content">
        <div className="landing-preview__heading">My Trips</div>
        <div className="landing-preview__subheading">
          Create, review, and track every visa journey from one place.
        </div>
        <div className="landing-preview__trip-grid">
          <div className="landing-preview__trip-card">
            <div className="landing-preview__trip-topline">
              <strong>Spain</strong>
              <span className="landing-preview__pill">3 travelers</span>
            </div>
            <div className="landing-preview__muted">2026-08-10 to 2026-08-24</div>
            <div className="landing-preview__list">
              <span>Lina Hassan Farouk</span>
              <span>Omar Nabil Rahman</span>
              <span>Mariam Adel Fathy</span>
            </div>
          </div>
          <div className="landing-preview__trip-card landing-preview__trip-card--ghost">
            <div>
              <strong>Add new trip</strong>
              <div className="landing-preview__muted">Create another visa journey</div>
            </div>
            <div className="landing-preview__plus">+</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DocumentsPreview() {
  const rows = [
    ['schengen-visa-photo-2026-05-14.jpg', 'General', '14/05/2026, 10:32:18'],
    ['employment-letter-april-2026.pdf', 'General', '14/05/2026, 10:18:42'],
    ['hotel-booking-barcelona.pdf', 'General', '11/05/2026, 09:41:03'],
    ['passport-scan.pdf', 'Passport', '11/05/2026, 09:36:25'],
  ];

  return (
    <div className="landing-preview landing-preview--panel">
      <div className="landing-preview__content landing-preview__content--full">
        <div className="landing-preview__panel-header">
          <div>
            <div className="landing-preview__heading landing-preview__heading--sm">Documents</div>
            <div className="landing-preview__subheading">
              Keep reusable travel documents organized by traveler and status.
            </div>
          </div>
          <div className="landing-preview__selector">Lina Hassan Farouk</div>
        </div>
        <div className="landing-preview__table">
          <div className="landing-preview__table-head">
            <span>Document</span>
            <span>Type</span>
            <span>Uploaded</span>
          </div>
          {rows.map((row) => (
            <div className="landing-preview__table-row" key={row[0]}>
              <span>
                <strong>{row[0]}</strong>
                <em>Click row to open</em>
              </span>
              <span>
                <span className="landing-preview__pill">{row[1]}</span>
              </span>
              <span>{row[2]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RequirementsPreview() {
  const rows = [
    ['Attend visa appointment in person', 'Appointment', 'Not applicable'],
    ['Parental travel consent letter', 'Document', 'Pending AI Validation'],
    ['Signed Schengen application form', 'Online form', 'Not applicable'],
    ['Proof of accommodation in Spain', 'Document', 'Pending AI Validation'],
  ];

  return (
    <div className="landing-preview landing-preview--panel">
      <div className="landing-preview__content landing-preview__content--full">
        <div className="landing-preview__panel-strip">
          <div className="landing-preview__panel-copy">
            <strong>My Visa Packages</strong>
            <div className="landing-preview__muted">
              Get a ready to download, and ready to print visa package.
            </div>
          </div>
          <div className="landing-preview__cta-mini">Generate new package</div>
        </div>
        <div className="landing-preview__requirements">
          <div className="landing-preview__requirements-head">
            <span>Requirement</span>
            <span>Type</span>
            <span>AI Validation</span>
          </div>
          {rows.map((row) => (
            <div className="landing-preview__requirements-row" key={row[0]}>
              <span>{row[0]}</span>
              <span>
                <span className="landing-preview__pill">{row[1]}</span>
              </span>
              <span>
                <span
                  className={`landing-preview__pill ${row[2] === 'Pending AI Validation'
                    ? 'landing-preview__pill--warning'
                    : ''
                    }`}
                >
                  {row[2]}
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function OrdersPreview() {
  const rows = [
    ['Notarised translation', 'Birth certificate translation', 'Pending', '620 EGP'],
    ['Travel medical insurance', '30-day Schengen coverage', 'Pending', '1,480 EGP'],
    ['Printed visa package', 'Spain family application set', 'Pending', '125 EGP'],
    ['Appointment assistance', 'Consulate scheduling support', 'Delivering', '2,350 EGP'],
  ];

  return (
    <div className="landing-preview landing-preview--panel">
      <div className="landing-preview__content landing-preview__content--full">
        <div className="landing-preview__panel-header">
          <div>
            <div className="landing-preview__heading landing-preview__heading--sm">Orders</div>
            <div className="landing-preview__subheading">
              Track purchases, shipments, and operational follow-up in one place.
            </div>
          </div>
          <div className="landing-preview__button-mini">New order</div>
        </div>
        <div className="landing-preview__orders">
          <div className="landing-preview__orders-head">
            <span>Order</span>
            <span>Status</span>
            <span>Total</span>
          </div>
          {rows.map((row) => (
            <div className="landing-preview__orders-row" key={`${row[0]}-${row[1]}`}>
              <span>
                <strong>{row[0]}</strong>
                <em>{row[1]}</em>
              </span>
              <span>
                <span
                  className={`landing-preview__pill ${row[2] === 'Delivering' ? 'landing-preview__pill--info' : ''
                    }`}
                >
                  {row[2]}
                </span>
              </span>
              <span>{row[3]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function LandingPage({ account }) {
  const primaryHref = account ? PORTAL_ROUTES.trips : AUTH_ROUTES.signup;
  const secondaryHref = account ? PORTAL_ROUTES.documents : AUTH_ROUTES.login;
  const primaryLabel = account ? 'Open workspace' : 'Start your application';
  const secondaryLabel = account ? 'View document vault' : 'Sign in';

  return (
    <div className="landing-page">
      <header className="landing-header">
        <FullWidthSection>
          <div className="landing-header__inner">
            <div className="landing-logo">TA2SHERA</div>
            <nav className="landing-header__nav">
              <a href="#process">How it works</a>
              <a href="#capabilities">Capabilities</a>
              <a href="#services">Services</a>
              <a href="#trust">Why Ta2shera</a>
              <a href="#faq">FAQ</a>
            </nav>
            <div className="landing-header__actions">
              <Button component={RouterLink} to={AUTH_ROUTES.login} variant="text">
                Sign in
              </Button>
              <Button component={RouterLink} to={primaryHref} variant="contained">
                {primaryLabel}
              </Button>
            </div>
          </div>
        </FullWidthSection>
      </header>

      <main>
        <section className="landing-hero">
          <FullWidthSection>
            <div className="landing-hero__grid">
              <div className="landing-hero__copy">
                <Chip className="landing-chip" icon={<AutoAwesome />} label="AI-assisted visa workspace" />
                <Typography component="h1" variant="h1">
                  Visa journeys, documents, and follow-up in one calm workspace.
                </Typography>
                <Typography color="text.secondary" variant="body1">
                  Ta2shera helps travelers and families prepare applications faster by organizing trips,
                  reusable documents, requirements, and service orders in one portal.
                </Typography>
                <Stack className="landing-hero__actions" direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button component={RouterLink} endIcon={<ArrowForward />} size="large" to={primaryHref} variant="contained">
                    {primaryLabel}
                  </Button>
                  <Button component={RouterLink} size="large" startIcon={<PlayArrow />} to={secondaryHref} variant="outlined">
                    {secondaryLabel}
                  </Button>
                </Stack>
                <div className="landing-hero__metrics">
                  <div>
                    <strong>One workspace</strong>
                    <span>for trips, docs, requirements, and orders</span>
                  </div>
                  <div>
                    <strong>Family-ready</strong>
                    <span>keep every traveler organized together</span>
                  </div>
                  <div>
                    <strong>Reusable records</strong>
                    <span>upload once, reuse on future applications</span>
                  </div>
                </div>
              </div>
            </div>
          </FullWidthSection>
        </section>

        <section className="landing-section" id="process">
          <FullWidthSection>
            <div className="landing-section__heading">
              <span className="landing-eyebrow">Process</span>
              <Typography component="h2" variant="h2">
                A simpler flow from trip idea to ready-to-submit package
              </Typography>
            </div>
            <div className="landing-steps">
              {processSteps.map((step) => (
                <article className="landing-step-card" key={step.id}>
                  <span className="landing-step-card__id">Step {step.id}</span>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </article>
              ))}
            </div>
          </FullWidthSection>
        </section>

        <section className="landing-section landing-section--showcase" id="capabilities">
          <FullWidthSection>
            <div className="landing-section__heading">
              <span className="landing-eyebrow">Capabilities</span>
              <Typography component="h2" variant="h2">
                Everything your visa workflow needs, connected together
              </Typography>
            </div>
            <div className="landing-showcase-grid">
              <div className="landing-showcase-copy">
                <div className="landing-capability-grid">
                  {capabilityCards.map((card) => (
                    <article className="landing-capability-card" key={card.title}>
                      <h3>{card.title}</h3>
                      <p>{card.description}</p>
                    </article>
                  ))}
                </div>
              </div>
              <div className="landing-showcase-panels">
                <TripsPreview />
                <DocumentsPreview />
                <RequirementsPreview />
                <OrdersPreview />
              </div>
            </div>
          </FullWidthSection>
        </section>

        <section className="landing-section" id="services">
          <FullWidthSection>
            <div className="landing-section__heading">
              <span className="landing-eyebrow">Services</span>
              <Typography component="h2" variant="h2">
                Optional add-on services when you want hands-on help
              </Typography>
              <Typography color="text.secondary" variant="body1">
                Ta2shera is free to use as a workspace. When you need extra operational support, you can choose
                from add-on services that help move the application forward.
              </Typography>
            </div>
            <div className="landing-services-grid">
              {serviceCards.map((service) => (
                <article className="landing-service-card" key={service.title}>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                </article>
              ))}
            </div>
          </FullWidthSection>
        </section>

        <section className="landing-section" id="trust">
          <FullWidthSection>
            <div className="landing-trust">
              <div className="landing-trust__copy">
                <span className="landing-eyebrow">Why Ta2shera</span>
                <Typography component="h2" variant="h2">
                  AI does the heavy lifting, while the whole process stays visible and reviewable
                </Typography>
                <Typography color="text.secondary" variant="body1">
                  The experience is designed around clarity. You can see which documents exist, which requirements
                  still need work, and which paid services are already moving.
                </Typography>
                <div className="landing-trust__points">
                  {trustPoints.map((point) => (
                    <div className="landing-trust__point" key={point}>
                      <CheckCircleOutlined />
                      <span>{point}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="landing-trust__card">
                <div className="landing-trust__card-top">
                  <LockOutlined />
                  <span>Workspace confidence</span>
                </div>
                <div className="landing-trust__checklist">
                  <div>Trips linked to the right travelers</div>
                  <div>Documents stored in one reusable vault</div>
                  <div>Requirement readiness visible at a glance</div>
                  <div>Operational orders tracked to completion</div>
                </div>
              </div>
            </div>
          </FullWidthSection>
        </section>

        <section className="landing-section landing-section--faq" id="faq">
          <FullWidthSection className="landing-container--narrow">
            <div className="landing-section__heading landing-section__heading--center">
              <span className="landing-eyebrow">FAQ</span>
              <Typography component="h2" variant="h2">
                Common questions
              </Typography>
            </div>
            <div className="landing-faq">
              {faqItems.map((item) => (
                <Accordion disableGutters key={item.question}>
                  <AccordionSummary>{item.question}</AccordionSummary>
                  <AccordionDetails>{item.answer}</AccordionDetails>
                </Accordion>
              ))}
            </div>
          </FullWidthSection>
        </section>

        <section className="landing-cta">
          <FullWidthSection className="landing-container--narrow">
            <Box className="landing-cta__card">
              <Typography component="h2" variant="h2">
                Keep the visa process in one place
              </Typography>
              <Typography color="text.secondary" variant="body1">
                Start with a trip, upload the essentials, and let the workspace guide the rest.
              </Typography>
              <Stack className="landing-cta__actions" direction={{ xs: 'column', sm: 'row' }} justifyContent="center" spacing={2}>
                <Button component={RouterLink} endIcon={<ArrowForward />} size="large" to={primaryHref} variant="contained">
                  {primaryLabel}
                </Button>
                <Button component={RouterLink} size="large" to={secondaryHref} variant="outlined">
                  {secondaryLabel}
                </Button>
              </Stack>
            </Box>
          </FullWidthSection>
        </section>
      </main>

      <div aria-label="Current country availability" className="landing-sticky-status" role="status">
        <span aria-hidden="true" className="landing-sticky-status__flag">
          🇳🇱
        </span>
        <span>Currently live for The Netherlands</span>
      </div>
    </div>
  );
}

export default LandingPage;
